import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SupplierInfiniteCombobox } from '../suppliers/SupplierInfiniteCombobox';
import { useLocations } from '../../hooks/useWmsLocations';
import type {
  AdjustInventoryInput,
  InventoryItem,
  Location,
  ReasonCode,
  UpdateInventoryInput,
} from '../../types';

const DEFAULT_UNIT_OPTIONS = [
  { value: 'EA', label: 'Each (EA)' },
  { value: 'BOX', label: 'Box' },
  { value: 'ROLL', label: 'Roll' },
  { value: 'PCS', label: 'Pieces' },
  { value: 'KG', label: 'Kilograms' },
  { value: 'M', label: 'Meters' },
  { value: 'L', label: 'Liters' },
];

const EMPTY_UNIT_VALUE = '__none__';

const reasonCodes: { value: ReasonCode; label: string }[] = [
  { value: 'cycle_count', label: 'Cycle Count' },
  { value: 'damage', label: 'Damage' },
  { value: 'qc_fail', label: 'QC Fail' },
  { value: 'qc_pass', label: 'QC Pass' },
  { value: 'found', label: 'Found' },
  { value: 'lost', label: 'Lost' },
  { value: 'expired', label: 'Expired' },
  { value: 'correction', label: 'Correction' },
  { value: 'other', label: 'Other' },
];

function getLocationLabel(location: Pick<Location, 'full_code' | 'zone_code' | 'bin_code' | 'name'>): string {
  const code = location.full_code || [location.zone_code, location.bin_code].filter(Boolean).join('-');
  return location.name ? `${code} - ${location.name}` : code;
}

export interface InventoryEditSubmitData {
  update?: UpdateInventoryInput;
  adjustment?: AdjustInventoryInput;
}

interface InventoryEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: InventoryItem | null;
  onSubmit: (data: InventoryEditSubmitData) => Promise<void>;
  isLoading?: boolean;
}

export function InventoryEditModal({
  open,
  onOpenChange,
  item,
  onSubmit,
  isLoading,
}: InventoryEditModalProps) {
  const [name, setName] = useState('');
  const [locationId, setLocationId] = useState('');
  const [supplierId, setSupplierId] = useState<string | undefined>(undefined);
  const [supplierName, setSupplierName] = useState('');
  const [unitOfMeasure, setUnitOfMeasure] = useState('');
  const [newQuantity, setNewQuantity] = useState<number>(0);
  const [reasonCode, setReasonCode] = useState<ReasonCode>('cycle_count');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');

  const locationsQuery = useLocations({
    page_length: 100,
    enabled: open,
  });

  useEffect(() => {
    if (!open || !item) return;

    setName(item.sku_name || item.sku_details?.name?.en || '');
    setLocationId(item.location_id);
    setSupplierId(item.preferred_supplier_id || undefined);
    setSupplierName(item.preferred_supplier_name || '');
    setUnitOfMeasure(item.unit_of_measure || '');
    setNewQuantity(item.quantity_on_hand);
    setReasonCode('cycle_count');
    setReferenceNumber('');
    setNotes('');
  }, [item, open]);

  if (!item) return null;

  const rawLocations = locationsQuery.data?.locations || [];
  const hasCurrentLocation = rawLocations.some((location) => location.id === item.location_id);
  const locations = hasCurrentLocation
    ? rawLocations
    : [
      {
        id: item.location_id,
        full_code: item.full_location_code,
        zone_code: item.zone_code,
        bin_code: item.bin_code,
        name: undefined,
      } as Location,
      ...rawLocations,
    ];

  const unitOptions = unitOfMeasure && !DEFAULT_UNIT_OPTIONS.some((option) => option.value === unitOfMeasure)
    ? [{ value: unitOfMeasure, label: unitOfMeasure }, ...DEFAULT_UNIT_OPTIONS]
    : DEFAULT_UNIT_OPTIONS;

  const originalName = item.sku_name || item.sku_details?.name?.en || '';
  const originalSupplierId = item.preferred_supplier_id || undefined;
  const originalSupplierName = item.preferred_supplier_name || '';
  const originalUnitOfMeasure = item.unit_of_measure || '';

  const trimmedName = name.trim();
  const trimmedSupplierName = supplierName.trim();
  const metadataChanged = (
    trimmedName !== originalName
    || locationId !== item.location_id
    || supplierId !== originalSupplierId
    || trimmedSupplierName !== originalSupplierName
    || unitOfMeasure !== originalUnitOfMeasure
  );
  const quantityChanged = newQuantity !== item.quantity_on_hand;
  const difference = newQuantity - item.quantity_on_hand;
  const canSubmit = Boolean(trimmedName)
    && Boolean(locationId)
    && newQuantity >= 0
    && (metadataChanged || quantityChanged)
    && !isLoading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    await onSubmit({
      update: metadataChanged ? {
        sku_name: trimmedName,
        location_id: locationId,
        preferred_supplier_id: supplierId ?? null,
        preferred_supplier_name: trimmedSupplierName || null,
        unit_of_measure: unitOfMeasure || null,
      } : undefined,
      adjustment: quantityChanged ? {
        inventory_item_id: item.id,
        new_quantity: newQuantity,
        reason_code: reasonCode,
        reference_number: referenceNumber || undefined,
        notes: notes || undefined,
      } : undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Inventory Item</DialogTitle>
          <DialogDescription>
            Update the inventory details for {item.sku_code}.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="rounded-lg bg-muted p-3 text-sm space-y-1">
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">SKU:</span>
                <span className="font-mono text-right">{item.sku_code}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Current Qty:</span>
                <span className="font-semibold text-right">{item.quantity_on_hand}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Current Location:</span>
                <span className="font-mono text-right">{item.full_location_code || '—'}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="inventory_name">Name</Label>
              <Input
                id="inventory_name"
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, 255))}
                placeholder="Item name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="inventory_quantity">New Quantity</Label>
              <Input
                id="inventory_quantity"
                type="number"
                value={newQuantity === 0 ? '' : newQuantity}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setNewQuantity(0);
                    return;
                  }
                  const parsed = parseInt(value, 10);
                  if (!isNaN(parsed) && parsed >= 0) {
                    setNewQuantity(parsed);
                  }
                }}
                min={0}
                required
              />
              {quantityChanged && (
                <p
                  className={`text-sm ${
                    difference > 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {difference > 0 ? '+' : ''}
                  {difference} from current
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="inventory_location">Location</Label>
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger id="inventory_location">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {getLocationLabel(location)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Supplier</Label>
              <SupplierInfiniteCombobox
                value={supplierId}
                fallbackLabel={!supplierId ? supplierName || undefined : undefined}
                placeholder="No supplier"
                onChange={(value) => {
                  if (value === 'all') {
                    setSupplierId(undefined);
                    setSupplierName('');
                    return;
                  }
                  setSupplierId(value);
                }}
                onSupplierSelected={(supplier) => {
                  setSupplierName(supplier?.name || '');
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="inventory_unit">Unit</Label>
              <Select
                value={unitOfMeasure || EMPTY_UNIT_VALUE}
                onValueChange={(value) => setUnitOfMeasure(value === EMPTY_UNIT_VALUE ? '' : value)}
              >
                <SelectTrigger id="inventory_unit">
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EMPTY_UNIT_VALUE}>No unit</SelectItem>
                  {unitOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="inventory_reason_code">Reason</Label>
              <Select
                value={reasonCode}
                onValueChange={(value) => setReasonCode(value as ReasonCode)}
              >
                <SelectTrigger id="inventory_reason_code">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {reasonCodes.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="inventory_reference_number">Reference # (optional)</Label>
              <Input
                id="inventory_reference_number"
                placeholder="e.g., CC-2024-001"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="inventory_notes">Notes (optional)</Label>
              <Textarea
                id="inventory_notes"
                placeholder="Additional details about this update..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
